import React from "react";
import { ProgrammeSummaryStrip } from "../../../library/ProgrammeSummaryStrip";
import { usePmcVersionListForFilters } from "../../../../query/hooks/QueryHooksLinkedProgrammes";
import { useVersionSummary } from "../../../../query/hooks/QueryHooksProgramDetail";

/**
 * Version summary strip for a version's Details/Modules pages, using
 * version-scoped totals rather than the Versions list's PMC-wide ones, so an
 * inactive version's page still shows its own numbers.
 * @param {() => void} [onAction] Omit to hide the action button entirely.
 * @param {string} [actionDisabledReason] Tooltip shown while disabled.
 * @param {boolean} [isLocked] Page's own lock state, shown while the version list can't provide one.
 * Also: programmeId, versionId, actionLabel, actionIcon, actionDisabled (self-explanatory).
 */
function VersionSummaryStripContainer({
    programmeId,
    versionId,
    isLocked = false,
    onAction,
    actionLabel,
    actionIcon = "plus",
    actionDisabled = false,
    actionDisabledReason,
}) {
    const { data: versions, isLoading: isVersionLoading, isError: isVersionError } = usePmcVersionListForFilters(programmeId);
    const { data: summary, isLoading: isSummaryLoading } = useVersionSummary(programmeId, versionId);

    const version = versions.find((row) => Number(row.id) === Number(versionId));

    if (!version) {
        const noVersionMessage = isVersionLoading
            ? "Loading…"
            : (isVersionError ? "Unable to load this version" : "Version not found");

        // Keeps the page's action (e.g. Assign Module) usable even when the version list fails.
        if (onAction && !isVersionLoading) {
            return (
                <ProgrammeSummaryStrip
                    versionName={noVersionMessage}
                    isLocked={isLocked}
                    totals={summary}
                    isLoading={isSummaryLoading}
                    onAction={onAction}
                    actionLabel={actionLabel}
                    actionIcon={actionIcon}
                    actionDisabled={actionDisabled}
                    actionDisabledReason={actionDisabledReason}
                />
            );
        }

        return (
            <ProgrammeSummaryStrip
                hasVersion={false}
                noVersionMessage={noVersionMessage}
            />
        );
    }

    return (
        <ProgrammeSummaryStrip
            versionName={version.name}
            isActive={Number(version.id) === Number(version.version_id)}
            isLocked={Boolean(version.lock_version_settings)}
            coordinator={version.coordinator}
            isCoordinatorLoading={isVersionLoading}
            totals={summary}
            isLoading={isSummaryLoading}
            onAction={onAction}
            actionLabel={actionLabel}
            actionIcon={actionIcon}
            actionDisabled={actionDisabled}
            actionDisabledReason={actionDisabledReason}
        />
    );
}

export default VersionSummaryStripContainer;
